import { Box, LinearProgress, Snackbar } from '@mui/material';
import { useRef, useState } from 'react';
import Images from '../Utils/images';
import ImageCropper from '../Components/ImageCropper';
import { thumbnailSchma, pdfSchema } from '../Utils/yupvalidations';
import { notify } from '../Utils/toastify';
import { uploadToS3 } from '../Utils/AwsService';
import Loading from './Loading';
import heic2any from 'heic2any';
import DepartmentsCreate from './DepartmentsCreate';
import CreateUploadDoc from './CreateUploadDoc';
// import { commonUploadFn } from '../../../Utils/AwsService'

interface Props {
  setProgress?: any;
  setUrl: any;
  loading?: boolean;
  setLoad?: (f: boolean) => void;
  title?: string;
  descTxt?: string;
  ratio?: any;
  guideline?: boolean;
  type?: string;
  setDepartment?: any;
  onAddDocument?: any;
  selectedDeptId?: any;
  onlyDocs?: boolean;
  onlyImage?: boolean;
  uploadProgress?: any;
  disabled?: boolean;
  documents?: any[]; // add this
  showDeptSignup?: boolean;
  isCreateStudio?: boolean;
  noShowCropper?: boolean;
  setFileName?: any;
  trigger?: any;
}

/**
 * Handles file upload and image cropping functionality for the component.
 * @example
 * ImageCropperInput({
 *   setProgress: function(progress) { /* update progress */
export const ImageCropperInput = ({
  setProgress,
  setUrl,
  loading,
  setLoad,
  title,
  descTxt,
  ratio,
  guideline = true,
  type,
  setDepartment,
  onAddDocument,
  onlyDocs,
  onlyImage,
  uploadProgress,
  disabled,
  documents,
  showDeptSignup,
  isCreateStudio,
  noShowCropper,
  setFileName,
  trigger,
}: Props) => {
  const imageRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [imgUrl, setImgUrl] = useState<any | null>(null);
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string>('');

  const handleClick = (e: any) => {
    if (loading) return;
    if (imageRef && imageRef.current) {
      imageRef.current.value = ''; // clear previous file
      imageRef.current.click();
    }
  };

  const handleCroppedImage = (image: any) => {
    // 🛑 Check document upload limit
    const maxDocs = 20;
    if ((documents?.length ?? 0) >= maxDocs) {
      notify(`You can only attach up to ${maxDocs} documents.`, 'error');
      return;
    }

    handleUpload(image);
  };

  /**
   * Uploads a file to S3 and handles status updates and callbacks for adding the document.
   * @example
   * sync(file)
   * undefined
   * @param {File} file - File object representing the file to be uploaded.
   * @returns {void} No return value.
   * @description
   *   - Uses a callback function `onAddDocument` to send document details after upload.
   *   - Updates loading states through `setLoad` and `setLoadingStatus`.
   *   - Fetches the uploaded document's URL via `uploadToS3`.
   */
  const handleUpload = async (file: File) => {
    setLoad(true);
    setLoadingStatus(true);

    if (setFileName) {
      setFileName('documentName', selectedFileName || file?.name);
      // trigger();
    }

    const fileURL: any = await uploadToS3(file, setProgress);

    if (onAddDocument) {
      // Call the callback with document details
      onAddDocument({
        deptId: selectedDeptId, // Set this dynamically based on your application logic
        docName: file.name,
        docFormat: file.type,
        documentUrl: fileURL?.url,
      });
    }

    setUrl(fileURL?.url);
    setLoad(false);
    setLoadingStatus(false);
  };

  /**
   * Handles the selection and processing of image and document files from an input element.
   * @example
   * sync(event)
   * undefined
   * @param {React.ChangeEvent<HTMLInputElement>} e - The event triggered by file input change.
   * @returns {Promise<void>} Executes various operations based on file type; may notify user on errors.
   * @description
   *   - Supports various image formats, including 'jpeg', 'png', 'jpg', 'heic', and 'heif'.
   *   - Validates file size for image and document files to ensure they are below 100MB.
   *   - Limits the number of attachments to a maximum of 20 documents.
   *   - Converts HEIC/HEIF images to JPEG format and provides notification on failure.
   */

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const imageFormats: Array<string> = [
      'jpeg',
      'png',
      'jpg',
      'PNG',
      'JPEG',
      'HEIF',
      'heic',
      'HEIC',
      'heif',
    ];

    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileType = e.target.files[0]?.type?.split('/')[1]?.toLowerCase();
      const fileName = file.name;
      const fileExtension = fileName.split('.').pop()?.toLowerCase();
      const selectedFileSize = e.target.files[0]?.size / 1024 / 1024; // size in MB

      // ✅ ACTUAL FILE NAME FROM SELECTION
      setSelectedFileName(file?.name);

      // 🚫 Size validations
      if (imageFormats.includes(fileExtension) && selectedFileSize > 100) {
        notify('The image size should be max 100MB', 'error');
        return;
      }
      if (['pdf', 'PDF'].includes(fileType) && selectedFileSize > 100) {
        notify('The Document size should be max 100 mb', 'error');
        return;
      }

      // 🚫 Max document limit
      const maxDocs = 20;
      if ((documents?.length ?? 0) >= maxDocs) {
        notify(`You can only attach up to ${maxDocs} documents.`, 'error');
        return;
      }

      // 🚫 Restrict to images only
      if (onlyImage && !imageFormats.includes(fileType)) {
        notify('Invalid file type. Please select an image.', 'error');
        return;
      }

      // 📌 HEIC / HEIF case
      if (fileExtension === 'heic' || fileExtension === 'heif') {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
          });
          const convertedFile = new File(
            [convertedBlob as Blob],
            file.name.replace(/\.[^/.]+$/, '.jpeg'),
            { type: 'image/jpeg' },
          );

          await thumbnailSchma.validateAt('file', { file: convertedFile });

          if (noShowCropper) {
            handleUpload(convertedFile); // ✅ Direct upload
          } else {
            const reader = new FileReader();
            reader.onload = () => setImgUrl(reader.result);
            reader.readAsDataURL(convertedFile);
          }
        } catch (error) {
          notify('Failed to process HEIC image.', 'error');
          console.error('HEIC Conversion Error:', error);
        }
        return;
      }

      try {
        // 📌 PDF case
        if (
          fileType === 'application/pdf' ||
          fileType === 'pdf' ||
          fileType === 'PDF'
        ) {
          await pdfSchema.validateAt('file', { file });
          handleUpload(file); // ✅ Direct upload
        }
        // 📌 JPG / PNG / JPEG case
        else if (['jpg', 'jpeg', 'png'].includes(fileExtension ?? '')) {
          await thumbnailSchma.validateAt('file', { file });

          if (noShowCropper) {
            handleUpload(file); // ✅ Direct upload
          } else {
            const reader = new FileReader();
            reader.onload = () => setImgUrl(reader.result);
            reader.readAsDataURL(file);
          }
        }
        // 📌 Other images (fallback)
        else {
          await thumbnailSchma.validateAt('file', { file });

          if (noShowCropper) {
            handleUpload(file); // ✅ Direct upload
          } else {
            const reader = new FileReader();
            reader.onload = () => setImgUrl(reader.result);
            reader.readAsDataURL(file);
          }
        }
      } catch (error: any) {
        if (error?.message) notify(error.message, 'error');
        console.error('Error uploading file:', error);
        setLoad(false);
        setLoadingStatus(false);
      }
    }
  };

  if (loadingStatus) {
    return <Loading type="div" />;
  }

  return imgUrl && !noShowCropper ? (
    <ImageCropper
      image={imgUrl}
      setImgUrl={setImgUrl}
      handleCroppedImage={(image: any) => handleCroppedImage(image)}
      ratio={ratio}
    />
  ) : (
    <div className={`imageItem ${disabled ? 'disabled' : ''}`}>
      <div className="selectimg_video">
        <input
          disabled={disabled}
          type="file"
          aria-hidden="true"
          ref={imageRef}
          accept={
            type
              ? '.jpg, .jpeg, .png, .pdf,.heic,.heif'
              : '.jpg, .jpeg, .png,.heic,.heif'
          }
          style={{ display: 'none' }}
          onChange={handleFileChange}
          id="image_pdf_id"
        />

        <label onClick={type && !onlyDocs ? () => setOpen(true) : handleClick}>
          <img src={Images.IMAGE_IC} alt="Image" />
          <h4>
            {title ? title : 'Add Thumbnail'}
            {/* <sup> *</sup> */}
          </h4>
          {descTxt && <p>{descTxt}</p>}
          {guideline ? (
            <p>
              <span>
                Check the image upload guideline before uploading the image
                (Recommended).
              </span>
            </p>
          ) : null}
        </label>
      </div>

      {uploadProgress && uploadProgress !== null && uploadProgress != 100 && (
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          open={true}
          message="I love snacks"
          sx={{
            width: '300px',
            zIndex: 99,
          }}
          className="ImageProgressBg"
        >
          <Box width={'100%'} mt={1} p={2} className="imageProgress">
            <LinearProgress variant="determinate" value={uploadProgress} />
            <p>{uploadProgress}%</p>
          </Box>
        </Snackbar>
      )}

      {open && (
        <DepartmentsCreate
          open={open}
          handleClose={() => setOpen(false)}
          handleClick={() => setOpenUpload(true)}
          setSelectedDeptId={setSelectedDeptId}
          showDeptSignup={showDeptSignup}
          isCreateStudio={isCreateStudio}
        />
      )}

      {openUpload && (
        <CreateUploadDoc
          open={openUpload}
          handleClose={() => setOpenUpload(false)}
          selectedDeptId={selectedDeptId}
          onAddDocument={onAddDocument}
        />
      )}
    </div>
  );
};
